import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Alert, Snackbar,
  CircularProgress, Tooltip, Switch, FormControlLabel, Chip, Menu, ListItemIcon, ListItemText,
  Tabs, Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import ArticleIcon from '@mui/icons-material/Article';
import PaletteIcon from '@mui/icons-material/Palette';
import SettingsIcon from '@mui/icons-material/Settings';
import { HexColorPicker } from 'react-colorful';
import { useNavigate } from 'react-router-dom';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import firebaseDataService from '../../../services/FirebaseDataService';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import {
  OfferTemplateConfig, DEFAULT_TEMPLATE_CONFIG, DEFAULT_LAYOUT_CONFIG,
  TemplateLayoutConfig, TemplatePage, TemplateBlock, BlockType, PageType,
  mapTemplateFromApi, mapTemplateToApi,
  createBlock, createPage,
  CONTENT_BLOCK_CATALOG, OFFER_BLOCK_CATALOG,
} from '../../../types/offerTemplate.types';
import OfferPdfTemplate, { BranchInfoData, GivenOfferData } from './OfferPdfTemplate';
import ImageLibrarySelectionModal from '../inventory/ImageLibrarySelectionModal';
import BlockSettingsFactory from './templateBlocks/settings/BlockSettingsFactory';
import CoverPageSettingsPanel from './templateBlocks/settings/CoverPageSettings';
import { TEMPLATE_PRESETS } from './templatePresets';
import CanvasPageEditor from './canvasEditor/CanvasPageEditor';
import CanvasToolbar from './canvasEditor/CanvasToolbar';
import CanvasPropertiesPanel from './canvasEditor/CanvasPropertiesPanel';
import {
  CanvasElement, STAGE_WIDTH, STAGE_HEIGHT,
  createTextElement, createHeadingElement, createImageElement,
  createRectElement, createCircleElement, createLineElement, createStatBoxElement,
} from './canvasEditor/canvasElements';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

const FONT_OPTIONS = ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Calibri', 'Segoe UI'];

const MOCK_OFFER: GivenOfferData = {
  id: 'mock-1', offerNumber: 'TKL-2026-001', accountId: 'mock-account',
  account: { companyName: 'Örnek Müşteri A.Ş.', taxNumber: '1234567890', taxOffice: 'Beyoğlu', address: 'İstanbul, Türkiye', phone: '0212 555 00 00', email: 'info@ornekmusterias.com' },
  date: new Date().toISOString(), validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  subtotal: 15000, vatTotal: 2700, sctTotal: 0, discountTotal: 500, additionalDiscountAmount: 0, grandTotal: 17200,
  status: 'pending', currency: 'TRY', createdAt: new Date().toISOString(),
  items: [
    { id: '1', name: 'Web Tasarım Hizmeti', quantity: 1, unit: 'Adet', unitPrice: 8000, vatRate: 20, totalPrice: 8000, grossTotal: 9600 },
    { id: '2', name: 'SEO Optimizasyonu', quantity: 3, unit: 'Ay', unitPrice: 2500, vatRate: 20, discountRate: 10, totalPrice: 6750, grossTotal: 8100 },
    { id: '3', name: 'Logo Tasarımı', quantity: 1, unit: 'Adet', unitPrice: 1500, vatRate: 20, totalPrice: 1500, grossTotal: 1800 },
    { id: '4', name: 'Mobil Uygulama Geliştirme', quantity: 1, unit: 'Adet', unitPrice: 25000, vatRate: 20, totalPrice: 25000, grossTotal: 30000 },
    { id: '5', name: 'Sunucu Barındırma (1 Yıl)', quantity: 12, unit: 'Ay', unitPrice: 500, vatRate: 20, totalPrice: 6000, grossTotal: 7200 },
    { id: '6', name: 'SSL Sertifikası', quantity: 1, unit: 'Adet', unitPrice: 800, vatRate: 20, totalPrice: 800, grossTotal: 960 },
    { id: '7', name: 'E-posta Hosting', quantity: 10, unit: 'Kullanıcı', unitPrice: 150, vatRate: 20, totalPrice: 1500, grossTotal: 1800 },
    { id: '8', name: 'Güvenlik Duvarı Kurulumu', quantity: 1, unit: 'Adet', unitPrice: 3500, vatRate: 20, totalPrice: 3500, grossTotal: 4200 },
    { id: '9', name: 'Veri Yedekleme Sistemi', quantity: 1, unit: 'Adet', unitPrice: 2000, vatRate: 20, totalPrice: 2000, grossTotal: 2400 },
    { id: '10', name: 'Teknik Destek Paketi', quantity: 6, unit: 'Ay', unitPrice: 1200, vatRate: 20, discountRate: 5, totalPrice: 6840, grossTotal: 8208 },
    { id: '11', name: 'İçerik Yönetim Sistemi', quantity: 1, unit: 'Adet', unitPrice: 4500, vatRate: 20, totalPrice: 4500, grossTotal: 5400 },
    { id: '12', name: 'API Entegrasyonu', quantity: 3, unit: 'Adet', unitPrice: 3000, vatRate: 20, totalPrice: 9000, grossTotal: 10800 },
    { id: '13', name: 'Eğitim ve Danışmanlık', quantity: 2, unit: 'Gün', unitPrice: 2500, vatRate: 20, totalPrice: 5000, grossTotal: 6000 },
    { id: '14', name: 'Performans Optimizasyonu', quantity: 1, unit: 'Adet', unitPrice: 3000, vatRate: 20, totalPrice: 3000, grossTotal: 3600 },
    { id: '15', name: 'Yük Testi ve Raporlama', quantity: 1, unit: 'Adet', unitPrice: 1800, vatRate: 20, totalPrice: 1800, grossTotal: 2160 },
  ],
  notes: 'Örnek teklif notları burada görünecektir.',
};

const OfferTemplateEditor: React.FC = () => {
  const navigate = useNavigate();
  const previewRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<BranchInfoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [templates, setTemplates] = useState<OfferTemplateConfig[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [config, setConfig] = useState<OfferTemplateConfig>({ ...DEFAULT_TEMPLATE_CONFIG });
  const [isNewTemplate, setIsNewTemplate] = useState(false);

  const [showColorPicker, setShowColorPicker] = useState<'ana' | 'aksan' | null>(null);
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
  const [newTemplateDialogOpen, setNewTemplateDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);

  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [selectedBlockId, setSelectedBlockId] = useState<string>('');
  const [leftTab, setLeftTab] = useState(0);
  const [addBlockAnchor, setAddBlockAnchor] = useState<null | HTMLElement>(null);
  const [addPageAnchor, setAddPageAnchor] = useState<null | HTMLElement>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [canvasSelectedElId, setCanvasSelectedElId] = useState<string | null>(null);
  const [canvasImageLibOpen, setCanvasImageLibOpen] = useState(false);
  const canvasStageRef = useRef<any>(null);

  const layout: TemplateLayoutConfig = config.layoutConfig || DEFAULT_LAYOUT_CONFIG;
  const pages = [...layout.pages].sort((a, b) => a.order - b.order);
  const selectedPage = pages.find(p => p.id === selectedPageId);
  const blocks = selectedPage?.blocks ? [...selectedPage.blocks].sort((a, b) => a.order - b.order) : [];
  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  useEffect(() => {
    const init = async () => {
      try {
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) { setLoading(false); return; }
        const userData = await firebaseDataService.getUserData(uid);
        if (userData?.companyId) {
          setCompanyId(userData.companyId);
          const info = await getCompanyInfo(userData.companyId);
          if (info) setCompanyInfo(info as BranchInfoData);
        } else { setLoading(false); }
      } catch { setLoading(false); }
    };
    init();
  }, []);

  const loadTemplates = useCallback(async (preserveId?: string) => {
    if (!companyId) return;
    try {
      const res = await accountingApi.get(ACCOUNTING_API.OFFER_TEMPLATE_LIST);
      if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        const mapped = res.data.data.map(mapTemplateFromApi);
        setTemplates(mapped);
        const toSelect = preserveId
          ? mapped.find((t: OfferTemplateConfig) => t.id === preserveId)
          : mapped.find((t: OfferTemplateConfig) => t.isDefault) || mapped[0];
        if (toSelect) {
          setSelectedTemplateId(toSelect.id);
          setConfig(toSelect);
          const lc = toSelect.layoutConfig || DEFAULT_LAYOUT_CONFIG;
          if (lc.pages.length > 0) setSelectedPageId(lc.pages[0].id);
        }
        setIsNewTemplate(false);
      } else {
        setTemplates([]);
        setIsNewTemplate(true);
        setSelectedTemplateId('');
        setConfig({ ...DEFAULT_TEMPLATE_CONFIG, templateName: tt('Varsayılan Şablon', 'Default Template') });
        setSelectedPageId(DEFAULT_LAYOUT_CONFIG.pages[0]?.id || '');
      }
    } catch {
      setTemplates([]);
      setIsNewTemplate(true);
      setConfig({ ...DEFAULT_TEMPLATE_CONFIG, templateName: tt('Varsayılan Şablon', 'Default Template') });
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { if (companyId) loadTemplates(); }, [companyId, loadTemplates]);

  // ============================================================================
  // TEMPLATE CRUD
  // ============================================================================

  const handleSelectTemplate = (id: string) => {
    const tpl = templates.find(t => t.id === id);
    if (tpl) {
      setSelectedTemplateId(id);
      setConfig(tpl);
      setIsNewTemplate(false);
      const lc = tpl.layoutConfig || DEFAULT_LAYOUT_CONFIG;
      if (lc.pages.length > 0) setSelectedPageId(lc.pages[0].id);
      setSelectedBlockId('');
    }
  };

  const handleNewTemplate = () => { setNewTemplateName(''); setNewTemplateDialogOpen(true); };

  const handleCreateNewTemplate = () => {
    if (!newTemplateName.trim()) return;
    setConfig({ ...DEFAULT_TEMPLATE_CONFIG, templateName: newTemplateName.trim(), layoutConfig: { ...DEFAULT_LAYOUT_CONFIG } });
    setSelectedTemplateId('');
    setIsNewTemplate(true);
    setNewTemplateDialogOpen(false);
    setSelectedPageId(DEFAULT_LAYOUT_CONFIG.pages[0]?.id || '');
    setSelectedBlockId('');
  };

  const handleSave = async () => {
    if (!companyId) return;
    try {
      setSaving(true);
      const body = mapTemplateToApi(config);
      const shouldCreate = isNewTemplate || !config.id;
      if (shouldCreate) {
        const res = await accountingApi.post(ACCOUNTING_API.OFFER_TEMPLATE_CREATE, body);
        if (res.data?.success) {
          const created = mapTemplateFromApi(res.data.data);
          setSnackbar({ open: true, message: tt('Şablon başarıyla oluşturuldu.', 'Template created successfully.'), severity: 'success' });
          await loadTemplates(created.id);
        }
      } else {
        body.Id = config.id;
        const res = await accountingApi.post(ACCOUNTING_API.OFFER_TEMPLATE_UPDATE, body);
        if (res.data?.success) {
          setSnackbar({ open: true, message: tt('Şablon başarıyla güncellendi.', 'Template updated successfully.'), severity: 'success' });
          await loadTemplates(config.id);
        }
      }
    } catch (err: any) {
      const detail = err?.response?.data?.message || err?.message || '';
      setSnackbar({ open: true, message: `${tt('Kaydetme hatası', 'Save error')}: ${detail || tt('Bilinmeyen hata', 'Unknown error')}`, severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplateId) return;
    try {
      const res = await accountingApi.post(ACCOUNTING_API.OFFER_TEMPLATE_SET_DEFAULT(selectedTemplateId));
      if (res.data?.success) {
        setSnackbar({ open: true, message: tt('Varsayılan şablon güncellendi.', 'Default template updated.'), severity: 'success' });
        await loadTemplates();
      }
    } catch { setSnackbar({ open: true, message: tt('Hata oluştu.', 'An error occurred.'), severity: 'error' }); }
  };

  const handleDelete = async () => {
    if (!selectedTemplateId) return;
    try {
      const res = await accountingApi.post(ACCOUNTING_API.OFFER_TEMPLATE_DELETE, { Id: selectedTemplateId });
      if (res.data?.success) {
        setSnackbar({ open: true, message: tt('Şablon silindi.', 'Template deleted.'), severity: 'success' });
        setDeleteConfirmOpen(false);
        await loadTemplates();
      }
    } catch { setSnackbar({ open: true, message: tt('Silme sırasında hata oluştu.', 'An error occurred while deleting.'), severity: 'error' }); }
  };

  // ============================================================================
  // LAYOUT UPDATE HELPERS
  // ============================================================================

  const updateLayout = (newLayout: TemplateLayoutConfig) => {
    setConfig(prev => ({ ...prev, layoutConfig: newLayout }));
  };

  const updatePageSettings = (pageId: string, settings: Record<string, any>) => {
    const newPages = layout.pages.map(p => p.id === pageId ? { ...p, settings: { ...p.settings, ...settings } } : p);
    updateLayout({ pages: newPages });
  };

  const updateBlockSettings = (pageId: string, blockId: string, settings: Record<string, any>) => {
    const newPages = layout.pages.map(p => {
      if (p.id !== pageId) return p;
      const newBlocks = (p.blocks || []).map(b => b.id === blockId ? { ...b, settings: { ...b.settings, ...settings } } : b);
      return { ...p, blocks: newBlocks };
    });
    updateLayout({ pages: newPages });
  };

  const togglePageVisibility = (pageId: string) => {
    const newPages = layout.pages.map(p => p.id === pageId ? { ...p, visible: !p.visible } : p);
    updateLayout({ pages: newPages });
  };

  const toggleBlockVisibility = (pageId: string, blockId: string) => {
    const newPages = layout.pages.map(p => {
      if (p.id !== pageId) return p;
      const newBlocks = (p.blocks || []).map(b => b.id === blockId ? { ...b, visible: !b.visible } : b);
      return { ...p, blocks: newBlocks };
    });
    updateLayout({ pages: newPages });
  };

  const movePage = (pageId: string, direction: 'up' | 'down') => {
    const sorted = [...layout.pages].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(p => p.id === pageId);
    if (direction === 'up' && idx > 0) { [sorted[idx].order, sorted[idx - 1].order] = [sorted[idx - 1].order, sorted[idx].order]; }
    if (direction === 'down' && idx < sorted.length - 1) { [sorted[idx].order, sorted[idx + 1].order] = [sorted[idx + 1].order, sorted[idx].order]; }
    updateLayout({ pages: sorted });
  };

  const moveBlock = (pageId: string, blockId: string, direction: 'up' | 'down') => {
    const newPages = layout.pages.map(p => {
      if (p.id !== pageId) return p;
      const sorted = [...(p.blocks || [])].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex(b => b.id === blockId);
      if (direction === 'up' && idx > 0) { [sorted[idx].order, sorted[idx - 1].order] = [sorted[idx - 1].order, sorted[idx].order]; }
      if (direction === 'down' && idx < sorted.length - 1) { [sorted[idx].order, sorted[idx + 1].order] = [sorted[idx + 1].order, sorted[idx].order]; }
      return { ...p, blocks: sorted };
    });
    updateLayout({ pages: newPages });
  };

  const addPage = (type: PageType, title: string) => {
    const maxOrder = Math.max(0, ...layout.pages.map(p => p.order));
    const newPage = createPage(type, title);
    newPage.order = maxOrder + 1;
    updateLayout({ pages: [...layout.pages, newPage] });
    setSelectedPageId(newPage.id);
    setSelectedBlockId('');
    setAddPageAnchor(null);
  };

  const removePage = (pageId: string) => {
    const newPages = layout.pages.filter(p => p.id !== pageId);
    updateLayout({ pages: newPages });
    if (selectedPageId === pageId) {
      setSelectedPageId(newPages[0]?.id || '');
      setSelectedBlockId('');
    }
  };

  const addBlock = (type: BlockType) => {
    if (!selectedPageId) return;
    const newPages = layout.pages.map(p => {
      if (p.id !== selectedPageId) return p;
      const currentBlocks = p.blocks || [];
      const maxOrder = Math.max(0, ...currentBlocks.map(b => b.order));
      const newBlock = createBlock(type);
      newBlock.order = maxOrder + 1;
      return { ...p, blocks: [...currentBlocks, newBlock] };
    });
    updateLayout({ pages: newPages });
    setAddBlockAnchor(null);
  };

  const removeBlock = (pageId: string, blockId: string) => {
    const newPages = layout.pages.map(p => {
      if (p.id !== pageId) return p;
      return { ...p, blocks: (p.blocks || []).filter(b => b.id !== blockId) };
    });
    updateLayout({ pages: newPages });
    if (selectedBlockId === blockId) setSelectedBlockId('');
  };

  const applyPreset = (presetId: string) => {
    const preset = TEMPLATE_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    setConfig(prev => ({
      ...prev,
      ...preset.config,
      layoutConfig: JSON.parse(JSON.stringify(preset.layout)),
    }));
    setSelectedPageId(preset.layout.pages[0]?.id || '');
    setSelectedBlockId('');
    setPresetDialogOpen(false);
    setSnackbar({ open: true, message: `"${localizePresetName(preset.id, preset.name)}" ${tt("preset'i uygulandı.", 'preset applied.')}`, severity: 'success' });
  };
  const localizePageLabel = (pageType: PageType) => {
    if (pageType === 'coverPage') return tt('Kapak', 'Cover');
    if (pageType === 'contentPage') return tt('İçerik', 'Content');
    return tt('Teklif', 'Offer');
  };

  const localizePageTitle = (title: string) => {
    switch (title) {
      case 'Kapak': return tt('Kapak', 'Cover');
      case 'Giriş': return tt('Giriş', 'Introduction');
      case 'Biz Kimiz': return tt('Biz Kimiz', 'Who We Are');
      case 'Hizmetlerimiz': return tt('Hizmetlerimiz', 'Our Services');
      case 'Misyonumuz': return tt('Misyonumuz', 'Our Mission');
      case 'Çözümlerimiz': return tt('Çözümlerimiz', 'Our Solutions');
      case 'Teklif': return tt('Teklif', 'Offer');
      case 'Yeni Sayfa': return tt('Yeni Sayfa', 'New Page');
      default: return title;
    }
  };

  const localizePresetName = (presetId: string, fallback: string) => {
    switch (presetId) {
      case 'kurumsal': return tt('Kurumsal', 'Corporate');
      case 'modern': return tt('Modern', 'Modern');
      case 'minimalist': return tt('Minimalist', 'Minimalist');
      case 'detayli': return tt('Detaylı', 'Detailed');
      default: return fallback;
    }
  };

  const localizePresetDescription = (presetId: string, fallback: string) => {
    switch (presetId) {
      case 'kurumsal':
        return tt('Kapak + Firma tanıtımı + Teklif (SIBLY Hotel benzeri profesyonel döküman)', 'Cover + Company intro + Offer (SIBLY Hotel style professional document)');
      case 'modern':
        return tt('Kapak + Teklif sayfası, sade ve şık', 'Cover + Offer page, simple and stylish');
      case 'minimalist':
        return tt('Sadece teklif sayfası, kapak yok, temiz görünüm', 'Only offer page, no cover, clean look');
      case 'detayli':
        return tt('Kapak + Giriş + Hakkımızda + Misyon + Hizmetler + Çözümler + Teklif', 'Cover + Intro + About + Mission + Services + Solutions + Offer');
      default:
        return fallback;
    }
  };


  const updateGlobalConfig = (field: keyof OfferTemplateConfig, value: any) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // ============================================================================
  // CANVAS HELPERS
  // ============================================================================

  const isCanvasPage = selectedPage && (selectedPage.type === 'coverPage' || selectedPage.type === 'contentPage');

  const getCanvasElements = (): CanvasElement[] => {
    if (!selectedPage) return [];
    try {
      const raw = selectedPage.settings?.canvasElements;
      return Array.isArray(raw) ? raw : [];
    } catch { return []; }
  };

  const setCanvasElements = (elements: CanvasElement[]) => {
    if (!selectedPageId) return;
    updatePageSettings(selectedPageId, { canvasElements: elements });
  };

  const handleAddCanvasElement = (type: string) => {
    const els = getCanvasElements();
    let newEl: CanvasElement;
    switch (type) {
      case 'heading': newEl = createHeadingElement(); break;
      case 'text': newEl = createTextElement(); break;
      case 'image': newEl = createImageElement(); break;
      case 'rect': newEl = createRectElement(); break;
      case 'circle': newEl = createCircleElement(); break;
      case 'line': newEl = createLineElement(); break;
      case 'statBox': newEl = createStatBoxElement(); break;
      default: return;
    }
    if (!isTr) {
      if (type === 'heading') {
        newEl = { ...newEl, text: 'Heading' };
      } else if (type === 'text') {
        newEl = { ...newEl, text: 'Type text...' };
      } else if (type === 'statBox') {
        newEl = { ...newEl, statLabel: 'Customers' };
      }
    }
    setCanvasElements([...els, newEl]);
    setCanvasSelectedElId(newEl.id);
  };

  const handleDeleteCanvasElement = () => {
    if (!canvasSelectedElId) return;
    setCanvasElements(getCanvasElements().filter(e => e.id !== canvasSelectedElId));
    setCanvasSelectedElId(null);
  };

  const handleDuplicateCanvasElement = () => {
    if (!canvasSelectedElId) return;
    const els = getCanvasElements();
    const src = els.find(e => e.id === canvasSelectedElId);
    if (!src) return;
    const dup: CanvasElement = { ...src, id: `el-${Date.now()}-dup`, x: src.x + 20, y: src.y + 20 };
    setCanvasElements([...els, dup]);
    setCanvasSelectedElId(dup.id);
  };

  const handleMoveCanvasLayer = (direction: 'up' | 'down' | 'top' | 'bottom') => {
    if (!canvasSelectedElId) return;
    const els = [...getCanvasElements()];
    const idx = els.findIndex(e => e.id === canvasSelectedElId);
    if (idx < 0) return;
    const [item] = els.splice(idx, 1);
    if (direction === 'top') els.push(item);
    else if (direction === 'bottom') els.unshift(item);
    else if (direction === 'up' && idx < els.length) els.splice(idx + 1, 0, item);
    else if (direction === 'down' && idx > 0) els.splice(idx - 1, 0, item);
    else els.splice(idx, 0, item);
    setCanvasElements(els);
  };

  const handleAlignCanvasElement = (alignment: 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom') => {
    if (!canvasSelectedElId) return;
    const els = getCanvasElements().map(e => {
      if (e.id !== canvasSelectedElId) return e;
      const w = e.width || (e.radiusX ? e.radiusX * 2 : 100);
      const h = e.height || (e.radiusY ? e.radiusY * 2 : 100);
      switch (alignment) {
        case 'left': return { ...e, x: 0 };
        case 'centerH': return { ...e, x: (STAGE_WIDTH - w) / 2 };
        case 'right': return { ...e, x: STAGE_WIDTH - w };
        case 'top': return { ...e, y: 0 };
        case 'centerV': return { ...e, y: (STAGE_HEIGHT - h) / 2 };
        case 'bottom': return { ...e, y: STAGE_HEIGHT - h };
        default: return e;
      }
    });
    setCanvasElements(els);
  };

  const handleCanvasElementChange = (updates: Partial<CanvasElement>) => {
    if (!canvasSelectedElId) return;
    setCanvasElements(getCanvasElements().map(e => e.id === canvasSelectedElId ? { ...e, ...updates } : e));
  };

  const selectedCanvasEl = canvasSelectedElId ? getCanvasElements().find(e => e.id === canvasSelectedElId) || null : null;

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  }

  const blockCatalog = selectedPage?.type === 'offerPage' ? OFFER_BLOCK_CATALOG : CONTENT_BLOCK_CATALOG;
  const pageTypeIcon = (type: PageType) => {
    if (type === 'coverPage') return <DescriptionIcon fontSize="small" />;
    if (type === 'contentPage') return <ArticleIcon fontSize="small" />;
    return <ViewQuiltIcon fontSize="small" />;
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* TOP BAR */}
      <Paper elevation={1} sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, borderRadius: 0 }}>
        <Tooltip title={tt('Tekliflere Dön', 'Back to Offers')}><IconButton onClick={() => navigate('/teklifler')} size="small"><ArrowBackIcon /></IconButton></Tooltip>
        <Typography variant="h6" sx={{ fontWeight: 600, mr: 1, fontSize: '1rem' }}>{tt('Şablon Editörü', 'Template Editor')}</Typography>
        <GuideButton guideKey="documents.offerTemplateEditor" size="small" />
        <Divider orientation="vertical" flexItem />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{tt('Şablon', 'Template')}</InputLabel>
          <Select value={selectedTemplateId} label={tt('Şablon', 'Template')} onChange={(e) => handleSelectTemplate(e.target.value)}>
            {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.templateName} {t.isDefault && '⭐'}</MenuItem>)}
          </Select>
        </FormControl>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={handleNewTemplate}>{tt('Yeni', 'New')}</Button>
        <Button size="small" variant="outlined" onClick={() => setPresetDialogOpen(true)} startIcon={<PaletteIcon />}>{tt('Preset', 'Preset')}</Button>
        {selectedTemplateId && !isNewTemplate && (
          <>
            <Tooltip title={config.isDefault ? tt('Zaten varsayılan', 'Already default') : tt('Varsayılan Yap', 'Set as Default')}>
              <span><IconButton size="small" onClick={handleSetDefault} disabled={config.isDefault} color="warning">{config.isDefault ? <StarIcon /> : <StarBorderIcon />}</IconButton></span>
            </Tooltip>
            <Tooltip title={tt('Şablonu Sil', 'Delete Template')}><IconButton size="small" onClick={() => setDeleteConfirmOpen(true)} color="error"><DeleteIcon /></IconButton></Tooltip>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={saving || !config.templateName.trim()}>
          {isNewTemplate ? tt('Oluştur', 'Create') : tt('Kaydet', 'Save')}
        </Button>
      </Paper>

      {/* MAIN CONTENT */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* CANVAS TOOLBAR (only for cover/content pages) */}
        {isCanvasPage && (
          <CanvasToolbar
            onAddElement={handleAddCanvasElement}
            selectedId={canvasSelectedElId}
            elements={getCanvasElements()}
            onDeleteElement={handleDeleteCanvasElement}
            onDuplicateElement={handleDuplicateCanvasElement}
            onMoveLayer={handleMoveCanvasLayer}
            onAlignElement={handleAlignCanvasElement}
          />
        )}

        {/* LEFT PANEL */}
        <Paper elevation={0} sx={{ width: isCanvasPage ? 280 : 360, flexShrink: 0, overflow: 'hidden', borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
          <Tabs value={leftTab} onChange={(_, v) => setLeftTab(v)} variant="fullWidth" sx={{ flexShrink: 0, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5, fontSize: '0.75rem' } }}>
            <Tab icon={<ViewQuiltIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={tt('Sayfalar', 'Pages')} />
            <Tab icon={<SettingsIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={tt('Ayarlar', 'Settings')} />
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
            {leftTab === 0 && (
              <>
                {/* PAGE LIST */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>{tt('Sayfalar', 'Pages')}</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={(e) => setAddPageAnchor(e.currentTarget)}>{tt('Ekle', 'Add')}</Button>
                </Box>
                {pages.map((page, i) => (
                  <Paper
                    key={page.id}
                    variant="outlined"
                    onClick={() => { setSelectedPageId(page.id); setSelectedBlockId(''); setCanvasSelectedElId(null); }}
                    sx={{
                      p: 1, mb: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5,
                      bgcolor: selectedPageId === page.id ? 'primary.50' : 'transparent',
                      borderColor: selectedPageId === page.id ? 'primary.main' : 'divider',
                      opacity: page.visible ? 1 : 0.4,
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    {pageTypeIcon(page.type)}
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: selectedPageId === page.id ? 600 : 400, fontSize: '0.8rem' }}>{localizePageTitle(page.title)}</Typography>
                    <Chip label={localizePageLabel(page.type)} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); togglePageVisibility(page.id); }} sx={{ p: 0.25 }}>
                      {page.visible ? <VisibilityIcon sx={{ fontSize: 14 }} /> : <VisibilityOffIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                    <IconButton size="small" disabled={i === 0} onClick={(e) => { e.stopPropagation(); movePage(page.id, 'up'); }} sx={{ p: 0.25 }}><ArrowUpwardIcon sx={{ fontSize: 14 }} /></IconButton>
                    <IconButton size="small" disabled={i === pages.length - 1} onClick={(e) => { e.stopPropagation(); movePage(page.id, 'down'); }} sx={{ p: 0.25 }}><ArrowDownwardIcon sx={{ fontSize: 14 }} /></IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); removePage(page.id); }} sx={{ p: 0.25 }} color="error"><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                  </Paper>
                ))}

                {selectedPage && (
                  <>
                    <Divider sx={{ my: 1.5 }} />

                    {isCanvasPage ? (
                      /* Canvas Properties Panel for Cover/Content pages */
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                          {tt('Sayfa', 'Page')}: {localizePageTitle(selectedPage.title)}
                        </Typography>
                        <TextField fullWidth size="small" label={tt('Sayfa Başlığı', 'Page Title')} sx={{ mb: 1 }} value={selectedPage.title}
                          onChange={(e) => {
                            const newPages = layout.pages.map(p => p.id === selectedPageId ? { ...p, title: e.target.value } : p);
                            updateLayout({ pages: newPages });
                          }}
                        />
                        <TextField fullWidth size="small" label={tt('Arka Plan Rengi', 'Background Color')} sx={{ mb: 1 }} value={selectedPage.settings?.bgColor || '#ffffff'}
                          onChange={(e) => updatePageSettings(selectedPageId, { bgColor: e.target.value })}
                          InputProps={{ startAdornment: <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: selectedPage.settings?.bgColor || '#ffffff', border: '1px solid #ccc', mr: 1, flexShrink: 0 }} /> }}
                        />
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                          {tt('Eleman Özellikleri', 'Element Properties')}
                        </Typography>
                        <CanvasPropertiesPanel
                          element={selectedCanvasEl}
                          onChange={handleCanvasElementChange}
                          onOpenImageLibrary={() => setCanvasImageLibOpen(true)}
                        />
                      </>
                    ) : selectedPage.type === 'offerPage' ? (
                      /* Block list for Offer pages */
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {selectedPage.title} - Bloklar
                          </Typography>
                          <Button size="small" startIcon={<AddIcon />} onClick={(e) => setAddBlockAnchor(e.currentTarget)}>{tt('Ekle', 'Add')}</Button>
                        </Box>
                        {blocks.map((block, i) => (
                          <Paper
                            key={block.id}
                            variant="outlined"
                            onClick={() => setSelectedBlockId(block.id)}
                            sx={{
                              p: 0.75, mb: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5,
                              bgcolor: selectedBlockId === block.id ? 'primary.50' : 'transparent',
                              borderColor: selectedBlockId === block.id ? 'primary.main' : 'divider',
                              opacity: block.visible ? 1 : 0.4,
                              '&:hover': { borderColor: 'primary.light' },
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.75rem', fontWeight: selectedBlockId === block.id ? 600 : 400 }}>
                              {[...CONTENT_BLOCK_CATALOG, ...OFFER_BLOCK_CATALOG].find(c => c.type === block.type)?.label || block.type}
                            </Typography>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleBlockVisibility(selectedPage.id, block.id); }} sx={{ p: 0.25 }}>
                              {block.visible ? <VisibilityIcon sx={{ fontSize: 12 }} /> : <VisibilityOffIcon sx={{ fontSize: 12 }} />}
                            </IconButton>
                            <IconButton size="small" disabled={i === 0} onClick={(e) => { e.stopPropagation(); moveBlock(selectedPage.id, block.id, 'up'); }} sx={{ p: 0.25 }}><ArrowUpwardIcon sx={{ fontSize: 12 }} /></IconButton>
                            <IconButton size="small" disabled={i === blocks.length - 1} onClick={(e) => { e.stopPropagation(); moveBlock(selectedPage.id, block.id, 'down'); }} sx={{ p: 0.25 }}><ArrowDownwardIcon sx={{ fontSize: 12 }} /></IconButton>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeBlock(selectedPage.id, block.id); }} sx={{ p: 0.25 }} color="error"><DeleteIcon sx={{ fontSize: 12 }} /></IconButton>
                          </Paper>
                        ))}
                        {selectedBlock && (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>{tt('Blok Ayarları', 'Block Settings')}</Typography>
                            <BlockSettingsFactory
                              block={selectedBlock}
                              onChange={(s) => updateBlockSettings(selectedPageId, selectedBlockId, s)}
                              config={config}
                            />
                          </>
                        )}
                      </>
                    ) : null}
                  </>
                )}
              </>
            )}

            {leftTab === 1 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>{tt('Şablon Bilgileri', 'Template Information')}</Typography>
                <TextField fullWidth size="small" label={tt('Şablon Adı', 'Template Name')} sx={{ mb: 2 }} value={config.templateName} onChange={(e) => updateGlobalConfig('templateName', e.target.value)} />
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>{tt('Marka / Görsel', 'Brand / Visual')}</Typography>
                <TextField fullWidth size="small" label={tt('Slogan', 'Slogan')} sx={{ mb: 1.5 }} value={config.slogan || ''} onChange={(e) => updateGlobalConfig('slogan', e.target.value)} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {config.logoUrl && (
                    <Box sx={{ position: 'relative' }}>
                      <img src={config.logoUrl} alt="Logo" style={{ height: 40, maxWidth: 120, objectFit: 'contain', borderRadius: 4, border: '1px solid #e0e0e0' }} />
                      <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: '#fff', '&:hover': { bgcolor: 'error.dark' }, width: 20, height: 20 }} onClick={() => updateGlobalConfig('logoUrl', '')}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                    </Box>
                  )}
                  <Button variant="outlined" size="small" startIcon={<ImageIcon />} onClick={() => setImageLibraryOpen(true)}>{config.logoUrl ? tt('Değiştir', 'Change') : tt('Logo Seç', 'Select Logo')}</Button>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>{tt('Renkler', 'Colors')}</Typography>
                {(['ana', 'aksan'] as const).map(type => {
                  const field = type === 'ana' ? 'anaRenk' : 'aksanRenk';
                  const label = type === 'ana' ? tt('Ana Renk', 'Primary Color') : tt('Aksan Renk', 'Accent Color');
                  const value = config[field];
                  return (
                    <Box key={type} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Box onClick={() => setShowColorPicker(showColorPicker === type ? null : type)} sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: value, border: '2px solid #ddd', cursor: 'pointer', flexShrink: 0 }} />
                        <TextField size="small" value={value} sx={{ flex: 1 }} onChange={(e) => updateGlobalConfig(field, e.target.value)} inputProps={{ maxLength: 7 }} />
                      </Box>
                      {showColorPicker === type && (
                        <Box sx={{ mt: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                          <HexColorPicker color={value} onChange={(c) => updateGlobalConfig(field, c)} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>{tt('Tipografi', 'Typography')}</Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <InputLabel>Font</InputLabel>
                  <Select value={config.font} label="Font" onChange={(e) => updateGlobalConfig('font', e.target.value)}>
                    {FONT_OPTIONS.map(f => <MenuItem key={f} value={f} sx={{ fontFamily: f }}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField fullWidth size="small" label={tt('Font Boyutu (px)', 'Font Size (px)')} type="number" sx={{ mb: 2 }} value={config.fontBoyutu} onChange={(e) => updateGlobalConfig('fontBoyutu', Math.max(10, Math.min(18, parseInt(e.target.value) || 13)))} inputProps={{ min: 10, max: 18 }} />
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>{tt('Teklif Ayarları', 'Offer Settings')}</Typography>
                <TextField fullWidth size="small" label={tt('Ödeme Koşulları', 'Payment Terms')} multiline rows={2} sx={{ mb: 1.5 }} value={config.odemeKosullari || ''} onChange={(e) => updateGlobalConfig('odemeKosullari', e.target.value)} />
                <TextField fullWidth size="small" label={tt('Geçerlilik Notu', 'Validity Note')} sx={{ mb: 1.5 }} value={config.gecerlilikNotu || ''} onChange={(e) => updateGlobalConfig('gecerlilikNotu', e.target.value)} />
                <TextField fullWidth size="small" label={tt('Alt Bilgi Notu', 'Footer Note')} multiline rows={2} sx={{ mb: 1 }} value={config.altBilgiNotu || ''} onChange={(e) => updateGlobalConfig('altBilgiNotu', e.target.value)} />
              </>
            )}
          </Box>
        </Paper>

        {/* RIGHT PANEL */}
        <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: '#e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {isCanvasPage ? (
            /* CANVAS EDITOR for Cover/Content pages */
            <Box sx={{ p: 2 }}>
              <Box sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                <CanvasPageEditor
                  elements={getCanvasElements()}
                  onChange={setCanvasElements}
                  bgColor={selectedPage?.settings?.bgColor || '#ffffff'}
                  selectedId={canvasSelectedElId}
                  onSelectElement={setCanvasSelectedElId}
                  stageRef={canvasStageRef}
                />
              </Box>
            </Box>
          ) : (
            /* OFFER PAGE PREVIEW */
            <Box sx={{ p: 3 }}>
              <Box sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                <OfferPdfTemplate ref={previewRef} config={config} companyInfo={companyInfo} offer={MOCK_OFFER} offerType="given" />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ADD PAGE MENU */}
      <Menu anchorEl={addPageAnchor} open={Boolean(addPageAnchor)} onClose={() => setAddPageAnchor(null)}>
        <MenuItem onClick={() => addPage('coverPage', tt('Kapak', 'Cover'))}><ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon><ListItemText>{tt('Kapak Sayfası', 'Cover Page')}</ListItemText></MenuItem>
        <MenuItem onClick={() => addPage('contentPage', tt('Yeni Sayfa', 'New Page'))}><ListItemIcon><ArticleIcon fontSize="small" /></ListItemIcon><ListItemText>{tt('İçerik Sayfası', 'Content Page')}</ListItemText></MenuItem>
        <MenuItem onClick={() => addPage('offerPage', tt('Teklif', 'Offer'))}><ListItemIcon><ViewQuiltIcon fontSize="small" /></ListItemIcon><ListItemText>{tt('Teklif Sayfası', 'Offer Page')}</ListItemText></MenuItem>
      </Menu>

      {/* ADD BLOCK MENU */}
      <Menu anchorEl={addBlockAnchor} open={Boolean(addBlockAnchor)} onClose={() => setAddBlockAnchor(null)}>
        {blockCatalog.map(item => (
          <MenuItem key={item.type} onClick={() => addBlock(item.type)}>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* PRESET DIALOG */}
      <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{tt('Hazır Şablon Seç', 'Choose a Ready Template')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>{tt('Preset uygulamak mevcut layout yapınızı değiştirecektir.', 'Applying a preset will replace your current layout.')}</Alert>
          {TEMPLATE_PRESETS.map(preset => (
            <Paper key={preset.id} variant="outlined" sx={{ p: 2, mb: 1, cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' } }} onClick={() => applyPreset(preset.id)}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{localizePresetName(preset.id, preset.name)}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{localizePresetDescription(preset.id, preset.description)}</Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                {preset.layout.pages.map(p => <Chip key={p.id} label={localizePageTitle(p.title)} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />)}
              </Box>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setPresetDialogOpen(false)}>{tt('İptal', 'Cancel')}</Button></DialogActions>
      </Dialog>

      {/* IMAGE LIBRARY MODAL - Logo */}
      <ImageLibrarySelectionModal open={imageLibraryOpen} onClose={() => setImageLibraryOpen(false)}
        onSave={(urls) => { if (urls.length > 0) updateGlobalConfig('logoUrl', urls[0]); setImageLibraryOpen(false); }}
        companyId={companyId} productName={tt('Şablon Logo', 'Template Logo')} currentImageUrls={config.logoUrl ? [config.logoUrl] : []} productId={null} singleSelect={true}
      />

      {/* IMAGE LIBRARY MODAL - Canvas Element */}
      <ImageLibrarySelectionModal open={canvasImageLibOpen} onClose={() => setCanvasImageLibOpen(false)}
        onSave={(urls) => { if (urls.length > 0 && canvasSelectedElId) handleCanvasElementChange({ src: urls[0] }); setCanvasImageLibOpen(false); }}
        companyId={companyId} productName={tt('Canvas Görseli', 'Canvas Image')} currentImageUrls={selectedCanvasEl?.src ? [selectedCanvasEl.src] : []} productId={null} singleSelect={true}
      />

      {/* NEW TEMPLATE DIALOG */}
      <Dialog open={newTemplateDialogOpen} onClose={() => setNewTemplateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('Yeni Şablon Oluştur', 'Create New Template')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label={tt('Şablon Adı', 'Template Name')} sx={{ mt: 1 }} value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleCreateNewTemplate()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTemplateDialogOpen(false)}>{tt('İptal', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleCreateNewTemplate} disabled={!newTemplateName.trim()}>{tt('Oluştur', 'Create')}</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{tt('Şablonu Sil', 'Delete Template')}</DialogTitle>
        <DialogContent><Typography>{isTr ? <><strong>{config.templateName}</strong> şablonunu silmek istediğinize emin misiniz?</> : <>Are you sure you want to delete template <strong>{config.templateName}</strong>?</>}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{tt('İptal', 'Cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>{tt('Sil', 'Delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default OfferTemplateEditor;
